
///
/// EarthBrowser
///
/// Created by Matt Giger on 8/11/2024
/// Copyright (c) 2024 Geodata Labs
///

import { AsyncQueue } from '@/earthbrowser/utils/AsyncQueue'
import { logger } from '@/utils/LogDispatcher'
import SQLiteWorker from '@/workers/sqlite/index.js?worker'
import { type Remote, wrap } from 'comlink'

// Re-export diagnostics for convenience
export { DatabaseDiagnostics, type DiagnosticResult } from './DatabaseDiagnostics'

/*
 * this is the protocol for accessing the sqlite worker
 */
interface SQLiteWorkerProtocol {
	destroy(): Promise<void>
	load(name: string, volume: string, options?: { memory?: boolean }): Promise<void>
	reload(): Promise<void>
	exec(sql: string, params?: unknown[]): Promise<unknown[]>
	bulkInsert(sql: string, params: unknown[][]): Promise<unknown[]>
	bulkExec(commands: { sql: string, params?: unknown[] }[]): Promise<void>
	version(): Promise<string>
	close(): Promise<void>
	delete(): Promise<void>
	setVisibility(isVisible: boolean): void
}

/*
 * shape of cross-tab coordination messages posted by the sqlite worker
 */
interface WorkerLockMessage {
	type: string
	requesterId: string
}

export type Priority = "veryLow" | "low" | "medium" | "high" | "critical"

export interface SqliteExtensions {
	version: string
	fts5: boolean
	fts4: boolean
	fts3: boolean
	rtree: boolean
	geopoly: boolean
	json1: boolean
	mathFunctions: boolean
	soundex: boolean
	windowFunctions: boolean
	cte: boolean
	upsert: boolean
	generatedColumns: boolean
	sqliteGIS: boolean
	geos: boolean
}

// Store the database instance in a module-level variable to avoid Redux freezing
let databaseInstance: Database | null = null

export class Database {

	public static get db(): Database {
		if(!databaseInstance) {
			throw new Error("Database not found")
		}
		return databaseInstance
	}

	/**
	 * True when a database exists and is usable. Cache tables and loaders
	 * guard on this so builds that never initialize the database (the
	 * /embed globe-only entry) skip caching and fall through to network
	 * instead of throwing on the db getter.
	 */
	public static get available(): boolean {
		return databaseInstance !== null && !databaseInstance.failed
	}

	/**
	 * True when a persistent database exists for blob caching. Memory
	 * databases (embed builds with dataset layers) report false so the
	 * image/tile caches don't buffer multi-MB blobs in wasm RAM - those
	 * builds fall through to network fetch + the browser HTTP cache.
	 */
	public static get cacheAvailable(): boolean {
		return Database.available && !databaseInstance!.memory
	}

	public static getDb(): Database | null {
		return databaseInstance
	}

	public static setDb(db: Database | null): void {
		databaseInstance = db
	}

	public worker: Worker  // Made public for Python bridge
	private sqlite: Remote<SQLiteWorkerProtocol>
	private queue = new AsyncQueue<unknown>()
	private _failed: boolean = false
	private _hasLock: boolean = true
	private _memory: boolean = false
	private onLockRequestedCallback?: (requesterId: string) => void
	private onLockLostCallback?: () => void
	private onLockAcquiredCallback?: () => void
	private visibilityHandler?: () => void

	/**
	 * @param options.memory In-memory, non-persistent database with no OPFS
	 * storage and no cross-tab lock coordination (embed builds). The lock is
	 * always held, and lock/visibility handlers are skipped.
	 */
	constructor(name: string, volume: string, options?: { memory?: boolean }) {
		this.worker = new SQLiteWorker()
		this.sqlite = wrap<SQLiteWorkerProtocol>(this.worker)
		const memory = options?.memory === true
		this._memory = memory

		// Listen for messages from the worker about cross-tab coordination
		// (memory databases only ever emit LOCK_ACQUIRED)
		this.worker.addEventListener('message', (event) => {
			const data = event.data as WorkerLockMessage | undefined
			if (data?.type === 'LOCK_REQUESTED') {
				// Another tab is requesting access to this database
				if (this.onLockRequestedCallback) {
					this.onLockRequestedCallback(data.requesterId)
				}
			} else if (data?.type === 'LOCK_AUTO_RELEASED' || data?.type === 'LOCK_LOST') {
				// Lock was automatically released or lost
				this._hasLock = false
				if (this.onLockLostCallback) {
					this.onLockLostCallback()
				}
			} else if (data?.type === 'LOCK_ACQUIRED') {
				// Successfully acquired the database lock
				this._hasLock = true
				if (this.onLockAcquiredCallback) {
					this.onLockAcquiredCallback()
				}
			}
		})

		// Set up Page Visibility API and Window Focus to track when tab/window
		// is active. Memory databases skip this: visibility only matters for
		// cross-tab lock arbitration, which they opt out of.
		if (!memory) {
			const updateVisibility = () => {
				// Consider visible if: tab is visible AND window is focused
				const isVisible = !document.hidden && document.hasFocus()
				void this.sqlite.setVisibility(isVisible)
			}

			this.visibilityHandler = updateVisibility

			// Listen for both tab visibility and window focus changes
			document.addEventListener('visibilitychange', this.visibilityHandler)
			window.addEventListener('focus', this.visibilityHandler)
			window.addEventListener('blur', this.visibilityHandler)

			// Set initial visibility state
			updateVisibility()
		}

		void this.queue.add(async () => {
			try {
				await this.sqlite.load(name, volume, { memory })
				this._failed = false
				return true
			} catch(err) {
				this._failed = true
				logger.error(err)
				throw err
			}
		}, "critical")
	}

	public setOnLockRequested(callback: (requesterId: string) => void): void {
		this.onLockRequestedCallback = callback
	}

	public setOnLockLost(callback: () => void): void {
		this.onLockLostCallback = callback
	}

	public setOnLockAcquired(callback: () => void): void {
		this.onLockAcquiredCallback = callback
	}

	public async requestAccess(): Promise<void> {
		// Request to regain access to the database
		await this.sqlite.reload()
		this._failed = false
		this._hasLock = true
	}

	public get failed(): boolean {
		return this._failed
	}

	public get hasLock(): boolean {
		return this._hasLock
	}

	/** In-memory, non-persistent database (embed builds) */
	public get memory(): boolean {
		return this._memory
	}

	private get canExecute(): boolean {
		return !this._failed && this._hasLock
	}

	public async destroy(): Promise<void> {
		// Clean up visibility and focus handlers
		if (this.visibilityHandler) {
			document.removeEventListener('visibilitychange', this.visibilityHandler)
			window.removeEventListener('focus', this.visibilityHandler)
			window.removeEventListener('blur', this.visibilityHandler)
			this.visibilityHandler = undefined
		}

		if(this.canExecute) {
			this.queue.clear()
			await this.queue.add(async () => {
				try {
					await this.sqlite.destroy()
					this.worker.terminate()
				} catch(err) {
					logger.error(err)
				}
			}, "critical")
		}
	}

	public async exec(sql: string, params?: unknown[], priority: Priority = "medium"): Promise<unknown> {
		if(this.canExecute) {
			const result = await this.queue.add(async () => await this.sqlite.exec(sql, params), priority)
			return result
		}
		return []
	}

	public async execMany(commands: { sql: string, params?: unknown[]}[], priority: Priority = "medium"): Promise<void> {
		if(this.canExecute) {
			await this.queue.add(async () => {
				await this.sqlite.exec("begin transaction")
				for(const command of commands) {
					await this.sqlite.exec(command.sql, command.params)
				}
				await this.sqlite.exec("commit transaction")
			}, priority)
		}
	}

	public async bulkInsert(sql: string, params: unknown[][], priority: Priority = "medium"): Promise<void> {
		if(this.canExecute) {
			await this.queue.add(async () => await this.sqlite.bulkInsert(sql, params), priority)
		}
	}

	public async bulkExec(commands: { sql: string, params?: unknown[] }[], priority: Priority = "medium"): Promise<void> {
		if(this.canExecute) {
			await this.queue.add(async () => await this.sqlite.bulkExec(commands), priority)
		}
	}

	public enqueue(commands: { sql: string, params?: unknown[]}[], priority: Priority = "medium", completion?: () => Promise<void>): void {
		if(this.canExecute) {
			void this.queue.add(async () => {
				if(commands.length > 2) {
					await this.sqlite.exec("begin transaction")
				}
				for(const command of commands) {
					await this.sqlite.exec(command.sql, command.params)
				}
				if(commands.length > 2) {
					await this.sqlite.exec("commit transaction")
				}
				await completion?.()
			}, priority)
		}
	}

	public async version(): Promise<unknown> {
		if(this.canExecute) {
			return this.queue.add(async () => await this.sqlite.version(), "critical")
		}
	}

	/**
	 * SQLite extension support information
	 */
	public static readonly ExtensionNames: Record<keyof Omit<SqliteExtensions, 'version'>, string> = {
		fts5: 'FTS5',
		fts4: 'FTS4',
		fts3: 'FTS3',
		rtree: 'R*Tree',
		geopoly: 'GeoPoly',
		json1: 'JSON1',
		mathFunctions: 'Math Functions',
		soundex: 'SOUNDEX',
		windowFunctions: 'Window Functions',
		cte: 'CTE',
		upsert: 'UPSERT',
		generatedColumns: 'Generated Columns',
		sqliteGIS: 'SQLiteGIS',
		geos: 'GEOS',
	}

	/**
	 * Check which SQLite extensions are available
	 * @returns Object with extension availability and sqlite version info
	 */
	public async checkExtensions(): Promise<SqliteExtensions> {
		const result: SqliteExtensions = {
			version: '',
			fts5: false,
			fts4: false,
			fts3: false,
			rtree: false,
			geopoly: false,
			json1: false,
			mathFunctions: false,
			soundex: false,
			windowFunctions: false,
			cte: false,
			upsert: false,
			generatedColumns: false,
			sqliteGIS: false,
			geos: false,
		}

		if (!this.canExecute) return result

		try {
			// Get SQLite version
			const versionResult = await this.sqlite.version()
			result.version = String(versionResult)

			// Check for FTS5
			try {
				await this.sqlite.exec("CREATE VIRTUAL TABLE IF NOT EXISTS _ext_test USING fts5(content)")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.fts5 = true
			} catch { /* not available */ }

			// Check for FTS4
			try {
				await this.sqlite.exec("CREATE VIRTUAL TABLE IF NOT EXISTS _ext_test USING fts4(content)")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.fts4 = true
			} catch { /* not available */ }

			// Check for FTS3
			try {
				await this.sqlite.exec("CREATE VIRTUAL TABLE IF NOT EXISTS _ext_test USING fts3(content)")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.fts3 = true
			} catch { /* not available */ }

			// Check for R*Tree
			try {
				await this.sqlite.exec("CREATE VIRTUAL TABLE IF NOT EXISTS _ext_test USING rtree(id, minX, maxX, minY, maxY)")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.rtree = true
			} catch { /* not available */ }

			// Check for GeoPoly (extension of R*Tree)
			try {
				await this.sqlite.exec("CREATE VIRTUAL TABLE IF NOT EXISTS _ext_test USING geopoly()")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.geopoly = true
			} catch { /* not available */ }

			// Check for JSON1 functions
			try {
				await this.sqlite.exec("SELECT json('{}')")
				result.json1 = true
			} catch { /* not available */ }

			// Check for Math functions (added in SQLite 3.35.0)
			try {
				await this.sqlite.exec("SELECT sin(0), cos(0), tan(0), asin(0), acos(1), atan(0), pi()")
				result.mathFunctions = true
			} catch { /* not available */ }

			// Check for SOUNDEX
			try {
				await this.sqlite.exec("SELECT soundex('hello')")
				result.soundex = true
			} catch { /* not available */ }

			// Check for Window Functions (added in SQLite 3.25.0)
			try {
				await this.sqlite.exec("SELECT row_number() OVER () FROM (SELECT 1)")
				result.windowFunctions = true
			} catch { /* not available */ }

			// Check for CTE / WITH clause (added in SQLite 3.8.3)
			try {
				await this.sqlite.exec("WITH temp AS (SELECT 1) SELECT * FROM temp")
				result.cte = true
			} catch { /* not available */ }

			// Check for UPSERT (added in SQLite 3.24.0)
			try {
				await this.sqlite.exec("CREATE TABLE IF NOT EXISTS _ext_test (id INTEGER PRIMARY KEY, val TEXT)")
				await this.sqlite.exec("INSERT INTO _ext_test (id, val) VALUES (1, 'test') ON CONFLICT(id) DO UPDATE SET val = excluded.val")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.upsert = true
			} catch { /* not available */ }

			// Check for Generated Columns (added in SQLite 3.31.0)
			try {
				await this.sqlite.exec("CREATE TABLE IF NOT EXISTS _ext_test (a INTEGER, b INTEGER GENERATED ALWAYS AS (a * 2))")
				await this.sqlite.exec("DROP TABLE IF EXISTS _ext_test")
				result.generatedColumns = true
			} catch { /* not available */ }

			// Check for SQLiteGIS (PostGIS-inspired spatial functions)
			try {
				await this.sqlite.exec("SELECT ST_MakePoint(0, 0)")
				result.sqliteGIS = true
			} catch { /* not available */ }

			// Check for GEOS (Geometry Engine - advanced spatial operations)
			try {
				await this.sqlite.exec("SELECT ST_Intersects(ST_MakePoint(0, 0), ST_MakePoint(0, 0))")
				result.geos = true
			} catch { /* not available */ }

		} catch (error) {
			logger.error("Failed to check SQLite extensions:", error)
		}

		return result
	}

	/**
	 * @deprecated Use checkExtensions() instead
	 */
	public async checkFtsSupport(): Promise<{ fts5: boolean, fts4: boolean, fts3: boolean, version: string }> {
		const ext = await this.checkExtensions()
		return { fts5: ext.fts5, fts4: ext.fts4, fts3: ext.fts3, version: ext.version }
	}

	public async close(): Promise<unknown> {
		if(this.canExecute) {
			return this.queue.add(async () => await this.sqlite.close(), "critical")
		}
	}

	public async delete(): Promise<unknown> {
		if(this.canExecute) {
			return this.queue.add(async () => await this.sqlite.delete(), "critical")
		}
	}

	/**
	 * Run a quick database health check
	 * @returns Object with healthy status and optional error message
	 */
	public async quickHealthCheck(): Promise<{ healthy: boolean, error?: string }> {
		if (!this.canExecute) {
			return { healthy: false, error: "Database not available" }
		}

		try {
			const rows = await this.exec("PRAGMA quick_check") as { quick_check: string }[]
			const results = rows.map(r => r.quick_check)
			if (results.every(r => r === "ok")) {
				return { healthy: true }
			}
			return { healthy: false, error: results.join(", ") }
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			return { healthy: false, error: message }
		}
	}

	/**
	 * Run a full integrity check on the database
	 * @returns Array of check results ("ok" if healthy)
	 */
	public async integrityCheck(): Promise<string[]> {
		if (!this.canExecute) {
			return ["Database not available"]
		}

		try {
			const rows = await this.exec("PRAGMA integrity_check") as { integrity_check: string }[]
			return rows.map(r => r.integrity_check)
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			return [message]
		}
	}
}
